import { useFile } from "@/hooks/use-files";
import { cn } from "@/lib/utils";

interface UserAvatarProps {
  /** Pass the user domain object, not destructured primitives (see frontend rules). */
  user: { name?: string | null; image?: string | null };
  className?: string;
}

function initials(name?: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const joined = parts.map((p) => p.charAt(0).toUpperCase()).join("");
  return joined || "?";
}

/**
 * Renders a user's avatar. `user.image` may hold either a direct URL (e.g. from
 * an OAuth provider) or an uploaded file id (see `ProfileAvatarCard`, which
 * stores the id). File ids are resolved to a short-lived presigned URL; anything
 * unresolved falls back to the user's initials.
 */
export function UserAvatar({ user, className }: UserAvatarProps) {
  const image = user.image ?? null;
  const isDirectUrl =
    image !== null &&
    (image.startsWith("http") || image.startsWith("/") || image.startsWith("data:"));

  const { data } = useFile(isDirectUrl ? null : image);
  const src = isDirectUrl ? image : (data?.url ?? null);

  return (
    <span
      className={cn(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-xs font-medium text-primary",
        className,
      )}
    >
      {src ? (
        <img src={src} alt={user.name ?? "Avatar"} className="h-full w-full object-cover" />
      ) : (
        initials(user.name)
      )}
    </span>
  );
}
